import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth';

export default function Login() {
    const [tab, setTab] = useState<'login' | 'register'>('login');

    // Login fields
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Register fields
    const [regName, setRegName] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regPassword, setRegPassword] = useState('');
    const [regConfirm, setRegConfirm] = useState('');

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const { login } = useAuthStore();

    const completeLogin = (token: string, user: any, fallbackEmail: string) => {
        localStorage.setItem('auth_token', token);
        login(token, user || { id: '', email: fallbackEmail, full_name: fallbackEmail });
        navigate('/dashboard');
    };

    // ── Đăng nhập Email ───────────────────────────────────────────────────────
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const response = await api.login(email, password);
            const token = response.access_token || response.token;
            if (!token) throw new Error('Phản hồi không hợp lệ từ server');
            completeLogin(token, response.user, email);
        } catch (err: any) {
            setError(err.message || 'Đăng nhập thất bại');
        } finally {
            setLoading(false);
        }
    };

    // ── Đăng ký Email ─────────────────────────────────────────────────────────
    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (regPassword !== regConfirm) {
            setError('Mật khẩu xác nhận không khớp');
            return;
        }
        if (regPassword.length < 8) {
            setError('Mật khẩu phải có ít nhất 8 ký tự');
            return;
        }
        setLoading(true);
        try {
            const API_BASE = import.meta.env.VITE_API_URL || '/api';
            const res = await fetch(`${API_BASE}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                    full_name: regName,
                    email: regEmail,
                    password: regPassword,
                    password_confirmation: regConfirm,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                const msg = data.message || Object.values(data.errors || {})[0] || 'Đăng ký thất bại';
                throw new Error(Array.isArray(msg) ? msg[0] : msg);
            }
            const token = data.access_token || data.token;
            if (!token) throw new Error('Đăng ký thành công nhưng không nhận được token');
            completeLogin(token, data.user, regEmail);
        } catch (err: any) {
            setError(err.message || 'Đăng ký thất bại');
        } finally {
            setLoading(false);
        }
    };

    // ── Google OAuth ──────────────────────────────────────────────────────────
    const handleGoogleLogin = async () => {
        setError('');
        try {
            const res  = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/auth/google/redirect`);
            const data = await res.json();
            if (data.url) window.location.href = data.url;
        } catch {
            setError('Không thể kết nối Google. Vui lòng thử lại.');
        }
    };    const [showSuperForm, setShowSuperForm] = useState(false);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
            <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100 transition-all duration-300">
                {/* Logo */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-extrabold text-blue-600 tracking-tight">X_Game Admin</h1>
                    <p className="text-gray-500 mt-1.5 text-sm font-medium">Nền tảng Marketing Mini-Game hàng đầu</p>
                </div>

                {/* Error / Success Notifications */}
                {error && <div className="text-red-600 text-sm bg-red-50 border border-red-200 p-3.5 rounded-xl mb-5 font-medium">{error}</div>}
                {success && <div className="text-green-700 text-sm bg-green-50 border border-green-200 p-3.5 rounded-xl mb-5 font-medium">{success}</div>}

                {!showSuperForm ? (
                    <div className="space-y-6">
                        <div className="text-center bg-blue-50/50 rounded-xl p-4 border border-blue-100/40">
                            <p className="text-sm text-gray-600 leading-relaxed">
                                Đăng nhập nhanh bằng **tài khoản Google** để bắt đầu tạo chiến dịch và quản lý trò chơi của bạn.
                            </p>
                        </div>

                        {/* Google Button */}
                        <button
                            type="button"
                            onClick={handleGoogleLogin}
                            className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white text-gray-700 py-3 rounded-xl hover:bg-gray-50 hover:shadow-md transition-all font-semibold text-sm"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Đăng nhập bằng Google
                        </button>

                        <div className="pt-6 border-t border-gray-100 text-center">
                            <button
                                type="button"
                                onClick={() => { setShowSuperForm(true); setError(''); }}
                                className="text-xs text-gray-400 hover:text-blue-500 font-semibold transition-colors"
                            >
                                🖥️ Cổng đăng nhập cho Super Admin
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-5">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Super Admin Portal</h3>
                            <button
                                onClick={() => { setShowSuperForm(false); setError(''); }}
                                className="text-xs text-blue-600 hover:underline font-semibold"
                            >
                                ← Quay lại Google Login
                            </button>
                        </div>

                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Email Quản trị</label>
                                <input
                                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none text-sm transition-all"
                                    placeholder="superadmin@xgame.com" required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Mật khẩu</label>
                                <input
                                    type="password" value={password} onChange={e => setPassword(e.target.value)}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none text-sm transition-all"
                                    placeholder="••••••••" required
                                />
                            </div>
                            <button
                                type="submit" disabled={loading}
                                className="w-full bg-blue-600 text-white py-2.5 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 font-bold text-sm shadow-sm"
                            >
                                {loading ? 'Đang xác minh...' : 'Đăng nhập hệ thống'}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}
